// src/utils/productUtils.ts
import { Product } from '../types';
import { productHasTag } from './productTags';

/**
 * Checks if a product is available for checkout.
 * Now simply checks if the product exists and is visible.
 * (Previously depended on shiprocketId which is no longer needed)
 */
export const isProductAvailable = (product: Product): boolean => {
    return product.isVisible !== false;
};

/**
 * Checks if a product has the 'free' tag
 */
export const isFreeProduct = (product: Product): boolean => {
    return productHasTag(product.tags, 'free');
};

export const getProductCreatedTime = (product: Pick<Product, 'createdAt'>): number => {
    const createdTime = product.createdAt ? Date.parse(product.createdAt) : 0;
    return Number.isFinite(createdTime) ? createdTime : 0;
};

export const sortProductsByNewest = <T extends Pick<Product, 'createdAt'>>(products: T[]): T[] => {
    return [...products].sort((a, b) => getProductCreatedTime(b) - getProductCreatedTime(a));
};

/**
 * Returns a product with price set to 0 if it has the 'free' tag
 */
export const applyFreeProductPrice = (product: Product): Product => {
    if (isFreeProduct(product)) {
        // Use existing compare_at_price if valid, otherwise use the current price as the "original" price
        const originalPrice = (product.compare_at_price && product.compare_at_price > 0)
            ? product.compare_at_price
            : product.price;

        return { ...product, price: 0, compare_at_price: originalPrice };
    }
    return product;
};

/**
 * Applies free product pricing to an array of products
 */
export const applyFreeProductPrices = (products: Product[]): Product[] => {
    return products.map(applyFreeProductPrice);
};
